import {
  AI_CHAT_TIMELINE_PAGE_SIZE,
  aiChatQueryKeys,
} from "@/lib/data/react-query/ai-chat";
import type {
  AiChatItemBodyPart,
  AiChatLoadedItemBody,
  AiChatSearchHit,
  AiChatTimelineItem,
  AiChatTimelinePage,
  ChatApi,
} from "@/lib/types/ai-chat";
import { useQueryClient, type InfiniteData } from "@tanstack/react-query";
import { useCallback, useLayoutEffect, useRef, useState } from "react";

type LoadItemBody = (
  item: AiChatTimelineItem,
  part: AiChatItemBodyPart,
  options?: { throughClientLimit?: boolean; signal?: AbortSignal },
) => Promise<AiChatLoadedItemBody | undefined>;

type RevealRequest = {
  controller: AbortController;
  id: number;
  kind: "restoration" | "search";
};

const occurrenceIsLoaded = (
  text: string,
  query: string,
  occurrenceIndex: number,
) => {
  const haystack = text.toLowerCase();
  const needle = query.toLowerCase();
  let offset = haystack.indexOf(needle);
  let index = 0;
  while (offset !== -1) {
    if (index === occurrenceIndex) return true;
    index += 1;
    offset = haystack.indexOf(needle, offset + needle.length);
  }
  return false;
};

const highestRevision = (data: InfiniteData<AiChatTimelinePage> | undefined) =>
  data?.pages.reduce((highest, page) => Math.max(highest, page.revision), 0) ??
  0;

export const useAiChatSearchReveal = ({
  api,
  threadId,
  items,
  loadItemBody,
  setInteractionError,
  onBeforeNavigation,
}: {
  api: ChatApi;
  threadId?: string;
  items: AiChatTimelineItem[];
  loadItemBody: LoadItemBody;
  setInteractionError: (error: Error | null) => void;
  onBeforeNavigation?: () => void;
}) => {
  const queryClient = useQueryClient();
  const [isRevealingSearchHit, setIsRevealingSearchHit] = useState(false);
  const generationRef = useRef(0);
  const requestIdRef = useRef(0);
  const activeRequestRef = useRef<RevealRequest>();

  useLayoutEffect(() => {
    generationRef.current += 1;
    requestIdRef.current += 1;
    activeRequestRef.current?.controller.abort();
    activeRequestRef.current = undefined;
    setIsRevealingSearchHit(false);
  }, [threadId]);

  const revealItem = useCallback(
    async ({
      itemId,
      minimumRevision = 0,
      loadFullText = false,
      forceRefresh = false,
      query,
      occurrenceIndex = 0,
      requestKind,
    }: {
      itemId: string;
      minimumRevision?: number;
      loadFullText?: boolean;
      forceRefresh?: boolean;
      query?: string;
      occurrenceIndex?: number;
      requestKind: RevealRequest["kind"];
    }) => {
      if (!threadId) return false;
      onBeforeNavigation?.();
      if (occurrenceIndex >= 2_000) {
        const failure = new Error(
          "This item contains too many matches to display. Refine the search to open it.",
        );
        setInteractionError(failure);
        throw failure;
      }
      const generation = generationRef.current;
      const requestId = ++requestIdRef.current;
      activeRequestRef.current?.controller.abort();
      const abortController = new AbortController();
      activeRequestRef.current = {
        controller: abortController,
        id: requestId,
        kind: requestKind,
      };
      setIsRevealingSearchHit(true);
      setInteractionError(null);

      try {
        let item = items.find(
          (candidate) =>
            candidate.id === itemId && candidate.revision >= minimumRevision,
        );
        if (!item || forceRefresh) {
          const queryKey = aiChatQueryKeys.timeline(threadId);
          const page = await api.getTimeline(threadId, {
            aroundItemId: itemId,
            limit: AI_CHAT_TIMELINE_PAGE_SIZE,
            signal: abortController.signal,
          });
          if (
            generation !== generationRef.current ||
            requestId !== requestIdRef.current
          ) {
            return false;
          }
          await queryClient.cancelQueries({ queryKey, exact: true });
          if (
            generation !== generationRef.current ||
            requestId !== requestIdRef.current
          ) {
            return false;
          }
          item = page.items.find(
            (candidate) =>
              candidate.id === itemId && candidate.revision >= minimumRevision,
          );
          if (!item || page.threadId !== threadId) {
            throw new Error(
              "The matching conversation item could not be loaded.",
            );
          }

          const current =
            queryClient.getQueryData<InfiniteData<AiChatTimelinePage>>(
              queryKey,
            );
          if (highestRevision(current) > page.revision) {
            throw new Error(
              "The conversation changed while loading this match. Try again.",
            );
          }
          queryClient.setQueryData<InfiniteData<AiChatTimelinePage>>(queryKey, {
            pages: [page],
            pageParams: [{ direction: "around", itemId }],
          });
        }

        if (loadFullText && item.hasBody && item.text?.endsWith("…")) {
          const body = await loadItemBody(item, "text", {
            throughClientLimit: true,
            signal: abortController.signal,
          });
          if (!body) {
            throw new Error(
              "The matching message content could not be loaded.",
            );
          }
          if (query && !occurrenceIsLoaded(body.text, query, occurrenceIndex)) {
            throw new Error(
              body.reachedClientLimit
                ? "This match is beyond the conversation display limit. Refine the search to open it."
                : "This match changed and is no longer available. Search again to refresh the results.",
            );
          }
        }
        return true;
      } catch (error) {
        if (
          generation === generationRef.current &&
          requestId === requestIdRef.current &&
          !abortController.signal.aborted
        ) {
          const failure =
            error instanceof Error
              ? error
              : new Error(
                  "The matching conversation item could not be loaded.",
                );
          setInteractionError(failure);
          throw failure;
        }
        return false;
      } finally {
        if (
          generation === generationRef.current &&
          requestId === requestIdRef.current
        ) {
          activeRequestRef.current = undefined;
          setIsRevealingSearchHit(false);
        }
      }
    },
    [
      api,
      items,
      loadItemBody,
      queryClient,
      setInteractionError,
      threadId,
      onBeforeNavigation,
    ],
  );

  const revealTimelineItem = useCallback(
    async (itemId: string, minimumRevision = 0) => {
      const revealed = await revealItem({
        itemId,
        minimumRevision,
        forceRefresh: true,
        requestKind: "restoration",
      });
      if (!revealed) {
        throw new Error("Timeline restoration was interrupted.");
      }
    },
    [revealItem],
  );

  const revealSearchHit = useCallback(
    async (hit: AiChatSearchHit, query?: string, occurrenceIndex = 0) => {
      await revealItem({
        itemId: hit.itemId,
        minimumRevision: hit.revision,
        loadFullText: true,
        query,
        occurrenceIndex,
        requestKind: "search",
      });
    },
    [revealItem],
  );

  const cancelSearchReveal = useCallback(() => {
    const activeRequest = activeRequestRef.current;
    if (activeRequest?.kind !== "search") return;
    requestIdRef.current += 1;
    activeRequest.controller.abort();
    if (activeRequestRef.current?.id === activeRequest.id) {
      activeRequestRef.current = undefined;
    }
    setIsRevealingSearchHit(false);
  }, []);

  const cancelTimelineReveal = useCallback(() => {
    requestIdRef.current += 1;
    activeRequestRef.current?.controller.abort();
    activeRequestRef.current = undefined;
    setIsRevealingSearchHit(false);
  }, []);

  return {
    cancelSearchReveal,
    cancelTimelineReveal,
    isRevealingSearchHit,
    revealTimelineItem,
    revealSearchHit,
  };
};
